import DateTime from 'utils/datetime';

import type { Dayjs } from 'dayjs';
import type { TaskStatus } from 'types/common';
import type { TaskManagementFilters } from './hooks/useTaskManagementFilters';
import type { Task } from './types';

export const getTimeRemainingMessage = (date: string) => {
  const today = DateTime.now();
  const plannedDate = DateTime.toDayjs(date);

  const isOverDue = DateTime.isAfter(today, plannedDate);

  if (isOverDue) {
    const daysOverdue = DateTime.diffInCalendarDays(today, plannedDate);

    if (daysOverdue === 0) {
      return 'Due today';
    }
    if (daysOverdue > 5) {
      return `Overdue by 5+ days`;
    }
    return `Overdue by ${daysOverdue} day${daysOverdue > 1 ? 's' : ''}`;
  }

  const isDueInFuture = DateTime.isBefore(today, plannedDate);

  if (isDueInFuture) {
    const daysRemaining = DateTime.diffInCalendarDays(plannedDate, today);

    if (daysRemaining === 0) {
      return 'Due today';
    }

    return `Due in ${daysRemaining} day${daysRemaining > 1 ? 's' : ''}`;
  }

  return '';
};

export const convertTaskFiltersToApiParams = (
  filters?: TaskManagementFilters,
) => {
  const params: Record<string, string | number | boolean[]> = {};

  if (filters?.country) {
    params['country.equals'] = filters.country;
  }

  if (filters?.plantingGroupName) {
    params['plantingGroupName.contains'] = filters.plantingGroupName;
  }

  if (filters?.taskStatus?.length) {
    params['taskStatus.in'] = filters.taskStatus?.join(',') || '';
  }

  if (filters?.taskType?.length) {
    params['taskSubType.in'] = filters.taskType?.join(',') || '';
  }

  if (filters?.agronomist?.length) {
    params['juniorAgId.in'] = filters.agronomist?.join(',') || '';
  }

  if (filters?.manager?.length) {
    params['seniorAgId.in'] = filters.manager?.join(',') || '';
  }

  if (filters?.isTodaysTasks) {
    filters.dateRange = [
      DateTime.startOfDay(DateTime.nowInTimezone()),
      DateTime.endOfDay(DateTime.nowInTimezone()),
    ] as typeof filters.dateRange;
  }

  if (filters?.dateRange?.length === 2) {
    params['plannedDate.greaterThanOrEqual'] = DateTime.toDayjs(
      filters.dateRange[0] as Dayjs,
    )
      .endOf('day')
      .toISOString()
      .replace(/T.*Z/, 'T00:00:00Z');

    params['plannedDate.lessThanOrEqual'] = DateTime.toDayjs(
      filters?.dateRange[1] as Dayjs,
    )
      .endOf('day')
      .toISOString();
  }

  return params;
};

type SortPriority = { [key in TaskStatus]?: number };

export const sortByTaskStatus = (a: Task, b: Task) => {
  const sortPriority: SortPriority = {
    INCOMPLETE: 1,
    LATE: 2,
    ACTIVE: 3,
    COMPLETED: 4,
  };

  if (!a?.status && !b?.status) return 0;

  return (sortPriority[a?.status] || 99) - (sortPriority[b?.status] || 99);
};

export const sortByDue = (a: Task, b: Task) => {
  const dateA = DateTime.getIsoWithTimeAsStart(a?.plannedDate);
  const dateB = DateTime.getIsoWithTimeAsStart(b?.plannedDate);

  // Calculate: plannedDate - today
  // Negative = overdue, Positive = future, Zero = today
  const daysUntilA = DateTime.diffInCalendarDays(dateA, DateTime.now());
  const daysUntilB = DateTime.diffInCalendarDays(dateB, DateTime.now());

  // most negative (most overdue) first
  return daysUntilA - daysUntilB;
};

export const sortByDaysRemainingInTimeWindow = (a: Task, b: Task) => {
  const today = DateTime.now();

  // if first task has endDate and second doesn't, put first at the top
  if (a?.endDate && !b?.endDate) return -1;

  // if first task has no endDate and second has, put first at the end
  if (!a?.endDate && b?.endDate) return 1;

  // if neither have endDate, consider them equal
  if (!a?.endDate && !b?.endDate) return 0;

  // both have endDate, so compare days remaining to endDate
  const daysRemainingA = Math.max(
    DateTime.diffInCalendarDays(a?.endDate, today),
    0,
  );
  const daysRemainingB = Math.max(
    DateTime.diffInCalendarDays(b?.endDate, today),
    0,
  );

  // tasks with fewer days remaining come first
  return daysRemainingA - daysRemainingB;
};

export const calculateDatePercentage = (startDate: string, endDate: string) => {
  const today = DateTime.now();
  const totalDaysInTimeWindow =
    (DateTime.isAfter(startDate, endDate)
      ? DateTime.diffInCalendarDays(startDate, endDate)
      : DateTime.diffInCalendarDays(endDate, startDate)) || 0;
  const daysPassed = DateTime.diffInCalendarDays(today, startDate) || 0;

  const progress = Math.round(
    Math.min(Math.max((daysPassed / totalDaysInTimeWindow) * 100, 0), 100),
  );

  return progress;
};

export const mapStatus = (status?: string) => {
  if (status === 'INCOMPLETE') return 'failed';
  if (status === 'LATE') return 'overdue';
  return status ?? '';
};
